import { authFetch, withFileAuth } from './secureFiles';

// 대용량 파일 분할 병렬 다운로드
// 브라우저가 파일 저장 API(showSaveFilePicker)를 지원하면 Range 요청 여러 개로 받아 디스크에 바로 기록하고,
// 지원하지 않으면 일반 다운로드로 대체한다.
const THREAD_COUNT = 8;
// 청크마다 받은 내용을 메모리에 모았다가 한 번에 쓰므로 동시 메모리 사용량 ≈ THREAD_COUNT × CHUNK_SIZE
// (32MB 였을 때는 조각 배열 + 합친 사본까지 최대 ~512MB 를 잡아 메모리가 적은 PC 에서 할당 실패가 났다)
const CHUNK_SIZE = 16 * 1024 * 1024;
const MAX_CHUNK_RETRIES = 20;
const STALL_TIMEOUT_MS = 45000;
const PROGRESS_INTERVAL_MS = 200;

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

const parseDispositionFilename = (value) => {
    if (!value) return '';
    const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(value);
    if (utf8) try { return decodeURIComponent(utf8[1]); } catch {}
    return (/filename="([^"]+)"/i.exec(value) || [])[1] || '';
};

// eslint-disable-next-line no-control-regex
const safeName = (name) => String(name || 'download').replace(/[<>:"/\\|?*\x00-\x1F]/g, '_').slice(0, 180) || 'download';

const retryDelay = (res, attempt) => {
    const header = res?.headers?.get('Retry-After');
    if (header) {
        const seconds = Number(header);
        if (Number.isFinite(seconds)) return Math.max(1000, seconds * 1000);
        const at = Date.parse(header);
        if (Number.isFinite(at)) return Math.max(1000, at - Date.now());
    }
    return Math.min(45000, 1200 * 2 ** attempt);
};

export const nativeDownload = (url, fileName) => {
    const a = document.createElement('a');
    a.href = withFileAuth(url);
    a.download = safeName(fileName);
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
};

/**
 * @param {object} opts
 * @param {string} opts.url
 * @param {string} opts.label  화면 표시 이름 (파일명을 알 수 없을 때 대체)
 * @param {(p:{received:number,total:number,speed:number})=>void} opts.onProgress
 * @param {(chunks:string[])=>void} opts.onChunks  청크 상태 배열: idle|loading|done|error
 * @param {(text:string, kind?:string)=>void} opts.onStatus
 * @param {{cancelled:boolean, controllers:Set<AbortController>}} opts.control
 * @returns {Promise<'done'|'cancelled'|'native'|'fallback'>}  fallback: 분할 다운로드 실패로 기본 다운로드를 시작함 (이유는 control.fallbackReason)
 */
export async function parallelDownload({ url, label, onProgress, onChunks, onStatus, control }) {
    const head = await authFetch(url, { method: 'HEAD' });
    if (!head.ok) throw new Error(head.status === 404 ? '파일을 찾을 수 없습니다.' : `파일 정보를 가져올 수 없습니다. (${head.status})`);
    const total = parseInt(head.headers.get('Content-Length') || '0', 10);
    const supportsRange = head.headers.get('Accept-Ranges') === 'bytes';
    const fileName = safeName(parseDispositionFilename(head.headers.get('Content-Disposition')) || label);

    if (!supportsRange || !total || !window.showSaveFilePicker) {
        nativeDownload(url, fileName);
        return 'native';
    }

    let handle, writable;
    try {
        handle = await window.showSaveFilePicker({ suggestedName: fileName });
        writable = await handle.createWritable();
    } catch (err) {
        if (err?.name === 'AbortError') return 'cancelled';
        nativeDownload(url, fileName);
        return 'native';
    }

    const chunks = [];
    for (let start = 0; start < total; start += CHUNK_SIZE) chunks.push({ start, end: Math.min(start + CHUNK_SIZE, total) - 1 });
    const states = chunks.map(() => 'idle');
    const retries = chunks.map(() => 0);
    const queue = chunks.map((_, idx) => ({ idx, readyAt: 0 }));
    let received = 0, speedBytes = 0, speedAt = Date.now(), speed = 0;
    let writeQueue = Promise.resolve();
    let fatal = null, rateLimitUntil = 0, progressAt = 0;

    // 쓰기는 한 번에 하나씩. 실패한 쓰기가 뒤 순서의 쓰기까지 막지 않게 큐에는 실패를 삼킨 약속만 남기고,
    // 실패 자체는 그 청크를 기다리는 쪽에 던진다 (이전에는 쓰기 한 번 실패로 큐가 영영 reject 상태가 되어
    // 이후 모든 청크가 재시도 20번을 다 쓰고 "청크 N 다운로드 실패"로 멈췄다)
    const write = (position, data) => {
        const p = writeQueue.then(() => writable.write({ type: 'write', position, data }));
        writeQueue = p.catch(() => {});
        return p;
    };

    const setChunk = (idx, state) => { states[idx] = state; onChunks([...states]); };
    // 읽기마다(수십 KB) 화면을 다시 그리면 느린 PC 에서 탭이 버벅이므로 일정 간격으로만 알린다
    const progress = (force) => {
        const now = Date.now();
        const elapsed = (now - speedAt) / 1000;
        if (elapsed >= 0.5) { speed = speedBytes / elapsed; speedBytes = 0; speedAt = now; }
        if (!force && now - progressAt < PROGRESS_INTERVAL_MS) return;
        progressAt = now;
        onProgress({ received, total, speed });
    };
    onChunks([...states]);

    const downloadChunk = async (idx) => {
        const { start, end } = chunks[idx];
        const wait = rateLimitUntil - Date.now();
        if (wait > 0) await sleep(wait);
        if (control.cancelled) return;
        setChunk(idx, 'loading');
        const controller = new AbortController();
        control.controllers.add(controller);
        let got = 0, ok = false, stall = null;
        const touch = () => { clearTimeout(stall); stall = setTimeout(() => controller.abort(), STALL_TIMEOUT_MS); };
        try {
            touch();
            const res = await authFetch(url, { headers: { Range: `bytes=${start}-${end}` }, signal: controller.signal });
            if (res.status === 429) {
                rateLimitUntil = Math.max(rateLimitUntil, Date.now() + retryDelay(res, 0));
                throw Object.assign(new Error('429'), { rateLimited: true });
            }
            // 보안 프로그램·프록시가 Range 를 지우면 200 으로 파일 전체가 온다. 재시도해도 같으니 바로 포기한다
            if (res.status === 200) throw Object.assign(new Error('이 PC의 보안 프로그램·프록시가 분할 요청을 막고 있습니다'), { fatal: true });
            if (res.status !== 206 || res.headers.get('Content-Range') !== `bytes ${start}-${end}/${total}`) throw new Error(`range ${res.status}`);
            const size = end - start + 1;
            const buffer = new Uint8Array(size);
            const reader = res.body.getReader();
            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                if (control.cancelled) { reader.cancel(); return; }
                touch();
                if (got + value.byteLength > size) throw new Error(`long read ${got + value.byteLength}`);
                buffer.set(value, got);
                got += value.byteLength; received += value.byteLength; speedBytes += value.byteLength;
                progress();
            }
            if (got !== size) throw new Error(`short read ${got}`);
            clearTimeout(stall);
            try {
                await write(start, buffer);
            } catch (err) {
                // 쓰기 스트림은 한 번 실패하면 다시 쓸 수 없다 (디스크 공간 부족, 백신이 임시파일(.crswap)을 잠금 등)
                throw Object.assign(new Error(`디스크에 쓰지 못했습니다: ${err?.message || err?.name || err}`), { fatal: true });
            }
            ok = true;
            setChunk(idx, 'done');
        } finally {
            clearTimeout(stall);
            control.controllers.delete(controller);
            if (!ok && got && !control.cancelled) { received -= got; progress(true); }
        }
    };

    const nextItem = async () => {
        while (queue.length && !control.cancelled && !fatal) {
            const now = Date.now();
            const i = queue.findIndex(item => item.readyAt <= now);
            if (i >= 0) return queue.splice(i, 1)[0];
            await sleep(Math.min(1000, Math.max(100, Math.min(...queue.map(q => q.readyAt)) - now)));
        }
        return null;
    };

    const workers = Array.from({ length: Math.min(THREAD_COUNT, chunks.length) }, async () => {
        for (let item = await nextItem(); item; item = await nextItem()) {
            try {
                await downloadChunk(item.idx);
            } catch (err) {
                if (control.cancelled) return;
                retries[item.idx] += 1;
                setChunk(item.idx, 'error');
                // 메모리 할당 실패(RangeError)도 재시도해 봐야 같은 결과다
                if (err?.fatal || err instanceof RangeError) { fatal = fatal || err; return; }
                if (retries[item.idx] > MAX_CHUNK_RETRIES) { fatal = new Error(`청크 ${item.idx + 1} 다운로드 실패 (${err?.message || err})`); return; }
                onStatus('재시도 중입니다. 곧 재개되니 취소하지 마십시오.', 'retrying');
                queue.push({ idx: item.idx, readyAt: Date.now() + (err.rateLimited ? Math.max(rateLimitUntil - Date.now(), 1000) : retryDelay(null, retries[item.idx] - 1)) });
            }
        }
    });

    const discard = async () => {
        try { await writable.abort(); } catch {}
        try { await handle.remove?.(); } catch {}
    };
    try {
        await Promise.all(workers);
        if (fatal) throw fatal;
        await writeQueue;
        if (control.cancelled) { await discard(); return 'cancelled'; }
        progress(true);
        onStatus('파일을 마무리 중입니다. 창을 닫지 마십시오.');
        try {
            // 닫을 때 브라우저가 임시파일을 실제 파일로 옮기고 백신·세이프브라우징 검사를 하므로 여기서도 실패할 수 있다
            await writable.close();
        } catch (err) {
            throw new Error(`파일을 마무리하지 못했습니다: ${err?.message || err?.name || err}`);
        }
        return 'done';
    } catch (err) {
        await discard();
        if (control.cancelled) return 'cancelled';
        // 분할 다운로드가 이 PC 에서 안 되면 브라우저 기본 다운로드(이어받기 지원)로 다시 받는다
        control.fallbackReason = err?.message || String(err);
        nativeDownload(url, fileName);
        return 'fallback';
    }
}

// 관리자 업로드 파일 링크(/api/admin-download/.., 구버전 /admin_uploads/..) → 다운로드 API 경로. 외부 링크면 null
export const toAdminDownloadPath = (rawUrl) => {
    try {
        const u = new URL(rawUrl, window.location.origin);
        if (u.origin !== window.location.origin) return null;
        if (u.pathname.startsWith('/api/admin-download/')) return u.pathname;
        if (u.pathname.startsWith('/admin_uploads/')) return `/api/admin-download/${u.pathname.slice('/admin_uploads/'.length)}`;
    } catch {}
    return null;
};

export const formatBytes = (n) => {
    if (!Number.isFinite(n) || n <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
    return `${(n / 1024 ** i).toFixed(i >= 2 ? 1 : 0)} ${units[i]}`;
};
